/* Wearable Trigger — devicemotion-based shake detection that auto-starts
   the seizure stopwatch when the phone experiences a sustained violent shake
   pattern (>18 m/s² net accel over 6 samples within 1s window).

   iOS Safari requires an explicit permission grant per browser session, so
   there's a big "Arm hands-free trigger" button that requests it.
*/
import { useEffect, useRef } from "react";

const SHAKE_THRESHOLD = 18;      // m/s²  (gentle walk ≈ 1.5, hard shake ≈ 20+)
const HIT_COUNT = 6;             // consecutive readings above threshold
const WINDOW_MS = 1000;          // …within this window
const COOLDOWN_MS = 30000;       // don't retrigger within 30s of last event

export function useShakeTrigger({ enabled, onShake }) {
  const hits = useRef([]);
  const lastFire = useRef(0);

  useEffect(() => {
    if (!enabled) return;

    const handler = (e) => {
      const a = e.accelerationIncludingGravity || e.acceleration;
      if (!a || a.x == null) return;
      const mag = Math.sqrt(
        (a.x || 0) * (a.x || 0) + (a.y || 0) * (a.y || 0) + (a.z || 0) * (a.z || 0)
      );
      const now = Date.now();
      hits.current = hits.current.filter((t) => now - t < WINDOW_MS);
      if (mag > SHAKE_THRESHOLD) hits.current.push(now);
      if (hits.current.length >= HIT_COUNT && now - lastFire.current > COOLDOWN_MS) {
        lastFire.current = now;
        hits.current = [];
        onShake?.();
      }
    };
    window.addEventListener("devicemotion", handler);
    return () => window.removeEventListener("devicemotion", handler);
  }, [enabled, onShake]);
}

/** Request iOS Safari's per-session permission to read motion data. */
export async function requestShakePermission() {
  // iOS 13+
  if (typeof DeviceMotionEvent !== "undefined" &&
      typeof DeviceMotionEvent.requestPermission === "function") {
    try {
      const r = await DeviceMotionEvent.requestPermission();
      return r === "granted";
    } catch { return false; }
  }
  // Android / desktop — API is on by default
  return "DeviceMotionEvent" in window;
}


/* -----------------------------------------------------------------------
   Fall-Detect Escalation
   Watch device motion for `holdSeconds` after a shake-trigger fires. If
   the phone stays essentially still (net accel deviates from gravity by
   less than STILLNESS_THRESHOLD across ALL readings), we consider it a
   confirmed fall/loss-of-consciousness and fire onConfirm — the caller
   uses that to auto-send the WhatsApp ICE alert.

   Any real movement above STILLNESS_THRESHOLD before the countdown ends
   fires onCancel instead (user is upright & mobile → false alarm).
------------------------------------------------------------------------ */
const STILLNESS_THRESHOLD = 4;   // m/s² deviation from 9.8 gravity

export function useFallWatch({ enabled, holdSeconds = 60, onConfirm, onCancel }) {
  useEffect(() => {
    if (!enabled) return;
    const startedAt = Date.now();
    let cancelled = false;
    let confirmed = false;

    const handler = (e) => {
      if (cancelled || confirmed) return;
      const a = e.accelerationIncludingGravity;
      if (!a || a.x == null) return;
      const mag = Math.sqrt((a.x || 0) ** 2 + (a.y || 0) ** 2 + (a.z || 0) ** 2);
      const dev = Math.abs(mag - 9.8);
      // Ignore the first 1.5s so the tail-end of the triggering shake
      // doesn't immediately cancel the watch.
      if (Date.now() - startedAt < 1500) return;
      if (dev > STILLNESS_THRESHOLD) {
        cancelled = true;
        cleanup();
        onCancel?.("motion-detected");
      }
    };

    const timer = setInterval(() => {
      if (cancelled || confirmed) return;
      if (Date.now() - startedAt >= holdSeconds * 1000) {
        confirmed = true;
        cleanup();
        onConfirm?.();
      }
    }, 250);

    window.addEventListener("devicemotion", handler);
    const cleanup = () => {
      clearInterval(timer);
      window.removeEventListener("devicemotion", handler);
    };
    return cleanup;
     
  }, [enabled, holdSeconds]);
}
